import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import { get, pageContext } from 'ctm-util-functions';
import { Picture } from '../picture/picture';

const BrandLogo = (props) => {
    const { className, visualProps, variant, brandCode, srcPath, elementProps } = props;

    /**
     * Safely detect the logo code from the response object or apply a blank image.
     * @returns {String} A company code e.g. BUDD, ORIGIN, etc.
     */
    const getCompanyLogoCode = () => {
        return get(props, 'stateProps.product.providerMeta.code', brandCode || elementProps.brandCode) || 'BLANK';
    };

    /**
     * Returns the alt text, using product name if product is supplied, otherwise use altText provided in schema
     * @returns {String} - The name of the product, or an empty string
     */
    const getAltText = () => {
        return get(props, 'stateProps.product.productMeta.name', elementProps.altText) || '';
    };

    /**
     * A defined set of default props using the default size for non retina, and default size for retina.
     * By default, we use the 80x80 image for non-retina, and 160x160 image for retina
     * If we then need specific sized ones for specific breakpoints, we can pass them in
     * @returns {Object} A Picture component's visual props
     */
    const getElementProps = () => {
        const logoCode = getCompanyLogoCode();
        const altText = getAltText();
        // Get rid of any trailing slashes
        const baseAssetsPath = pageContext.getProps().models.pageSchemaModel.getBaseAssetsPath();
        const finalSrcPath = srcPath || elementProps.srcPath;
        let logoPath = finalSrcPath?.replace(/\/$/, '') || '/evt/assets/images/logos';
        if (logoPath.indexOf(baseAssetsPath) === -1) {
            logoPath = logoPath.replace('/evt/assets', baseAssetsPath);
        }
        const pathWithIdentifier = `${logoPath}/${logoCode}`;
        const sources = [
            {
                srcSet: `${pathWithIdentifier}.png 1x, ${pathWithIdentifier}@2x.png 2x`
            }
        ];
        return {
            source: sources,
            image: {
                alt: `${altText}`,
                src: `${pathWithIdentifier}.png`
            }
        };
    };

    /**
     * Render the picture component for company logos
     * @returns {Node} The picture component
     */

    if (!getCompanyLogoCode()) {
        return null;
    }

    return (
        <Picture
            className={className}
            elementProps={getElementProps()}
            visualProps={visualProps}
            variant={variant}
        />
    );
};

BrandLogo.propTypes = {
    // eslint-disable-next-line react/no-unused-prop-types
    stateProps: PropTypes.shape({
        product: PropTypes.instanceOf(Immutable.Map) // passed from container if use product from payload
    }),
    elementProps: PropTypes.shape({
        srcPath: PropTypes.string,
        brandCode: PropTypes.string,
        altText: PropTypes.string
    }),
    visualProps: PropTypes.shape({
        variant: PropTypes.string
    }),
    variant: PropTypes.string,
    className: PropTypes.string,
    srcPath: PropTypes.string,
    brandCode: PropTypes.string
};

BrandLogo.defaultProps = {
    elementProps: {}
};

export { BrandLogo };
